import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { fetchApiData, flushTestTables, flushProductionTables } from '../services/apiService';
import { processTicketLifecycle } from '../services/ticketService';
import { formatDate, formatDuration, formatDbDate } from '../utils/dateUtils';
import { config } from '../config';
import '../less/admin.less';

const Admin = () => {
  const [testApiResponse, setTestApiResponse] = useState(null);
  const [productionApiResponse, setProductionApiResponse] = useState(null);
  const [testDbLog, setTestDbLog] = useState([]);
  const [productionDbLog, setProductionDbLog] = useState([]);
  const [testActiveTickets, setTestActiveTickets] = useState([]);
  const [productionActiveTickets, setProductionActiveTickets] = useState([]);
  const [testTicketEvents, setTestTicketEvents] = useState([]);
  const [productionTicketEvents, setProductionTicketEvents] = useState([]);
  const [isLoadingTest, setIsLoadingTest] = useState(false);
  const [isLoadingProduction, setIsLoadingProduction] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Accordion states
  const [flushTablesExpanded, setFlushTablesExpanded] = useState(false);
  const [testApiResponseExpanded, setTestApiResponseExpanded] = useState(false);
  const [productionApiResponseExpanded, setProductionApiResponseExpanded] = useState(false);
  const [testDbLogExpanded, setTestDbLogExpanded] = useState(false);
  const [productionDbLogExpanded, setProductionDbLogExpanded] = useState(false);

  const logDbOperation = useCallback((operation, data = null, result = null, environment = 'test') => {
    const now = new Date();
    const pacificTime = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Los_Angeles',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    }).format(now);

    const logEntry = {
      timestamp: pacificTime,
      operation,
      data,
      result
    };

    if (environment === 'test') {
      setTestDbLog(prev => [...prev, logEntry]);
    } else {
      setProductionDbLog(prev => [...prev, logEntry]);
    }
  }, []);

  const handleFetchTestData = async (e) => {
    if (e) e.preventDefault();

    setIsLoadingTest(true);
    setError(null);

    try {
      logDbOperation('API Request Started', {
        environment: 'test',
        endpoint: 'status-entries',
        apiKeyLength: config.apiKey.length
      }, null, 'test');

      const response = await fetchApiData(config.apiKey, 'test', 'status-entries');

      if (response.error) {
        setError(`Test: ${response.error}`);
        logDbOperation('API Request Failed', {
          error: response.error,
          httpCode: response.http_code
        }, false, 'test');
        return;
      }

      setTestApiResponse(response);
      logDbOperation('API Request Successful', {
        httpCode: response.http_code,
        recordCount: response.response?.length || 0
      }, true, 'test');

      if (response.response && Array.isArray(response.response)) {
        const result = await processTicketLifecycle(
          response.response,
          'test',
          (op, data, res) => logDbOperation(op, data, res, 'test'),
          config.apiKey
        );

        const { tickets, events } = result;
        setTestActiveTickets(tickets);
        setTestTicketEvents(events);
      }
    } catch (err) {
      setError(`Test request failed: ${err.message}`);
      logDbOperation('API Request Exception', {
        error: err.message
      }, false, 'test');
    } finally {
      setIsLoadingTest(false);
    }
  };

  const handleFetchProductionData = async (e) => {
    if (e) e.preventDefault();

    setIsLoadingProduction(true);
    setError(null);

    try {
      logDbOperation('API Request Started', {
        environment: 'production',
        endpoint: 'status-entries',
        apiKeyLength: config.apiKey.length
      }, null, 'production');

      const response = await fetchApiData(config.apiKey, 'production', 'status-entries');

      if (response.error) {
        setError(`Production: ${response.error}`);
        logDbOperation('API Request Failed', {
          error: response.error,
          httpCode: response.http_code
        }, false, 'production');
        return;
      }

      setProductionApiResponse(response);
      logDbOperation('API Request Successful', {
        httpCode: response.http_code,
        recordCount: response.response?.length || 0
      }, true, 'production');

      if (response.response && Array.isArray(response.response)) {
        const result = await processTicketLifecycle(
          response.response,
          'production',
          (op, data, res) => logDbOperation(op, data, res, 'production'),
          config.apiKey
        );

        const { tickets, events } = result;
        setProductionActiveTickets(tickets);
        setProductionTicketEvents(events);
      }
    } catch (err) {
      setError(`Production request failed: ${err.message}`);
      logDbOperation('API Request Exception', {
        error: err.message
      }, false, 'production');
    } finally {
      setIsLoadingProduction(false);
    }
  };

  const handleFlushTestTables = async () => {
    if (!window.confirm('Are you sure you want to flush all TEST tables? This will delete ALL test data.')) {
      return;
    }

    setIsLoadingTest(true);
    setError(null);
    setSuccess(null);

    try {
      logDbOperation('Flush Test Tables Started', { environment: 'test' }, null, 'test');

      const result = await flushTestTables('test');

      if (result.success) {
        logDbOperation('Flush Test Tables Successful', {
          message: result.message,
          tablesAffected: result.tables_affected
        }, true, 'test');

        setTestActiveTickets([]);
        setTestTicketEvents([]);
        setTestApiResponse(null);
        setSuccess('Test tables flushed successfully!');
      } else {
        setError(`Test flush failed: ${result.error}`);
        logDbOperation('Flush Test Tables Failed', {
          error: result.error
        }, false, 'test');
      }
    } catch (err) {
      setError(`Test flush failed: ${err.message}`);
      logDbOperation('Flush Test Tables Exception', {
        error: err.message
      }, false, 'test');
    } finally {
      setIsLoadingTest(false);
    }
  };

  const handleFlushProductionTables = async () => {
    if (!window.confirm('⚠️ WARNING: Are you sure you want to flush all PRODUCTION tables? This will delete ALL production data and cannot be undone!')) {
      return;
    }

    setIsLoadingProduction(true);
    setError(null);
    setSuccess(null);

    try {
      logDbOperation('Flush Production Tables Started', { environment: 'production' }, null, 'production');

      const result = await flushProductionTables('production');

      if (result.success) {
        logDbOperation('Flush Production Tables Successful', {
          message: result.message,
          tablesAffected: result.tables_affected
        }, true, 'production');

        setProductionActiveTickets([]);
        setProductionTicketEvents([]);
        setProductionApiResponse(null);
        setSuccess('Production tables flushed successfully!');
      } else {
        const errorMessage = result.error || 'Unknown error occurred';
        setError(`Production flush failed: ${errorMessage}`);
        logDbOperation('Flush Production Tables Failed', {
          error: errorMessage,
          details: result
        }, false, 'production');
      }
    } catch (err) {
      setError(`Production flush failed: ${err.message}`);
      logDbOperation('Flush Production Tables Exception', {
        error: err.message
      }, false, 'production');
    } finally {
      setIsLoadingProduction(false);
    }
  };

  const getUniqueWorkCenterStatuses = (entries) => {
    const statuses = [];
    if (Array.isArray(entries)) {
      entries.forEach(entry => {
        if (entry.workcenterStatus && !statuses.includes(entry.workcenterStatus)) {
          statuses.push(entry.workcenterStatus);
        }
      });
    }
    return statuses.sort();
  };

  const renderApiResponseContent = (apiResponse, activeTickets, environment) => {
    if (apiResponse?.error) {
      return <div className="error">{apiResponse.error}</div>;
    }

    if (!apiResponse?.response) {
      return <p>No API response data available. Fetch data first.</p>;
    }

    const uniqueStatuses = getUniqueWorkCenterStatuses(apiResponse.response);

    return (
      <>
        <div className="raw-data">
          <h4>Raw Response Data:</h4>
          <textarea
            readOnly
            value={JSON.stringify(apiResponse, null, 2)}
          />
        </div>

        {uniqueStatuses.length > 0 && (
          <div className="status-list">
            <h4>Work Center Statuses:</h4>
            <ul>
              {uniqueStatuses.map((status, index) => (
                <li key={index}>{status}</li>
              ))}
            </ul>
          </div>
        )}

        <h4>Formatted Data:</h4>
        <table className="data-table">
          <thead>
            <tr>
              <th>Work Center</th>
              <th>Status</th>
              <th>Log Date</th>
              <th>Job Number</th>
              <th>Part Number</th>
              <th>Revision</th>
              <th>Operation</th>
              <th>Duration</th>
            </tr>
          </thead>
          <tbody>
            {apiResponse.response.map((entry, index) => (
              <tr key={index}>
                <td>{entry.workcenterCode || ''}</td>
                <td>{entry.workcenterStatus || ''}</td>
                <td>{formatDate(entry.logDate || '')}</td>
                <td>{entry.jobNumber || ''}</td>
                <td>{entry.partNumber || ''}</td>
                <td>{entry.revision || ''}</td>
                <td>{entry.operationCode || ''}</td>
                <td>{formatDuration(entry.duration || '')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </>
    );
  };

  const renderDbLogContent = (dbLog, ticketEvents) => {
    const sortedEvents = ticketEvents ? [...ticketEvents].sort((a, b) =>
      new Date(b.event_date) - new Date(a.event_date)
    ) : [];

    return (
      <>
        <div className="ticket-events-section">
          <h4>Ticket Events (Database Records)</h4>
          {sortedEvents.length === 0 ? (
            <p>No ticket events found.</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Ticket ID</th>
                  <th>Workcenter</th>
                  <th>Job Number</th>
                  <th>Event Sequence</th>
                  <th>Status</th>
                  <th>Event Date</th>
                  <th>Operation Code</th>
                  <th>Active</th>
                </tr>
              </thead>
              <tbody>
                {sortedEvents.map((event, index) => {
                  const status = event.status.toLowerCase();
                  const rowClass = ['alerted', 'acknowledged', 'evaluated'].includes(status)
                    ? `status-${status}`
                    : 'status-resolved';

                  return (
                    <tr key={index} className={rowClass}>
                      <td>{event.ticket_id}</td>
                      <td>{event.workcenter_code}</td>
                      <td>{event.job_number}</td>
                      <td>{event.event_sequence}</td>
                      <td>{event.status}</td>
                      <td>{formatDbDate(event.event_date)}</td>
                      <td>{event.operation_code || ''}</td>
                      <td>{event.is_active ? 'Yes' : 'No'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="db-log-summary">
          <h4>Summary</h4>
          <p><strong>Total Operations:</strong> {dbLog.length}</p>
          <p><strong>Total Ticket Events:</strong> {sortedEvents.length}</p>
        </div>

        <div className="db-operations">
          <h4>Operation Details</h4>
          {dbLog.length === 0 ? (
            <p>No database operations logged.</p>
          ) : (
            <table className="db-log-table">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Operation</th>
                  <th>Data</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {dbLog.map((log, index) => (
                  <tr key={index}>
                    <td>{log.timestamp}</td>
                    <td>{log.operation}</td>
                    <td>
                      {log.data ? (
                        <pre>{JSON.stringify(log.data, null, 2)}</pre>
                      ) : (
                        <em>No data</em>
                      )}
                    </td>
                    <td>
                      {log.result !== null ? (
                        typeof log.result === 'boolean' ? (
                          log.result ? '✅ Success' : '❌ Failed'
                        ) : (
                          <pre>{JSON.stringify(log.result, null, 2)}</pre>
                        )
                      ) : (
                        <em>No result</em>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </>
    );
  };

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>Admin Panel</h1>
        <Link to="/" className="back-link">
          <i className="fas fa-arrow-left"></i> Back to Dashboard
        </Link>
      </div>

      {error && (
        <div className="admin-message admin-error">
          <i className="fas fa-exclamation-circle"></i> {error}
        </div>
      )}

      {success && (
        <div className="admin-message admin-success">
          <i className="fas fa-check-circle"></i> {success}
        </div>
      )}

      <div className="admin-content">
        {/* Flush Tables Accordion */}
        <div className={`admin-accordion ${flushTablesExpanded ? 'expanded' : ''}`}>
          <div className="admin-accordion-header" onClick={() => setFlushTablesExpanded(!flushTablesExpanded)}>
            <span className="admin-accordion-icon">
              {flushTablesExpanded ? '▲' : '▼'}
            </span>
            <span className="admin-accordion-title">Flush Tables</span>
          </div>
          <div className="admin-accordion-content">
            <div className="flush-sections-container">
              <div className="flush-section">
                <h3>Test Environment</h3>
                <button
                  type="button"
                  onClick={handleFlushTestTables}
                  disabled={isLoadingTest}
                  className="danger-button"
                >
                  {isLoadingTest && <span className="loading-spinner"></span>}
                  <i className="fas fa-trash"></i> Flush Test Tables
                </button>
              </div>
              <div className="flush-section">
                <h3>Production Environment</h3>
                <div className="production-warning">
                  <div className="warning-text">
                    ⚠️ PRODUCTION DATA MANAGEMENT
                  </div>
                  <button
                    type="button"
                    onClick={handleFlushProductionTables}
                    disabled={isLoadingProduction}
                    className="danger-button production-button"
                  >
                    {isLoadingProduction && <span className="loading-spinner"></span>}
                    <i className="fas fa-exclamation-triangle"></i> Flush Production Tables
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Data Views Section */}
        <div className="data-views-section">
          <div className="data-views-header">
            <h2>Data Views</h2>
          </div>
          <div className="data-views-content">
            <div className="data-view-section">
              <h3>Test Environment</h3>
              <div className="data-view-actions">
                <button
                  type="button"
                  onClick={handleFetchTestData}
                  disabled={isLoadingTest}
                  className="fetch-button"
                >
                  {isLoadingTest && <span className="loading-spinner"></span>}
                  <i className="fas fa-sync"></i> Fetch Test Data
                </button>
              </div>
              <div className={`admin-accordion nested ${testApiResponseExpanded ? 'expanded' : ''}`}>
                <div className="admin-accordion-header" onClick={() => setTestApiResponseExpanded(!testApiResponseExpanded)}>
                  <span className="admin-accordion-icon">
                    {testApiResponseExpanded ? '▲' : '▼'}
                  </span>
                  <span className="admin-accordion-title">API Response (Test)</span>
                </div>
                <div className="admin-accordion-content api-response-content">
                  {renderApiResponseContent(testApiResponse, testActiveTickets, 'test')}
                </div>
              </div>
              <div className={`admin-accordion nested ${testDbLogExpanded ? 'expanded' : ''}`}>
                <div className="admin-accordion-header" onClick={() => setTestDbLogExpanded(!testDbLogExpanded)}>
                  <span className="admin-accordion-icon">
                    {testDbLogExpanded ? '▲' : '▼'}
                  </span>
                  <span className="admin-accordion-title">Database Operations (Test)</span>
                </div>
                <div className="admin-accordion-content">
                  {renderDbLogContent(testDbLog, testTicketEvents)}
                </div>
              </div>
            </div>
            <div className="data-view-section">
              <h3>Production Environment</h3>
              <div className="data-view-actions">
                <button
                  type="button"
                  onClick={handleFetchProductionData}
                  disabled={isLoadingProduction}
                  className="fetch-button"
                >
                  {isLoadingProduction && <span className="loading-spinner"></span>}
                  <i className="fas fa-sync"></i> Fetch Production Data
                </button>
              </div>
              <div className={`admin-accordion nested ${productionApiResponseExpanded ? 'expanded' : ''}`}>
                <div className="admin-accordion-header" onClick={() => setProductionApiResponseExpanded(!productionApiResponseExpanded)}>
                  <span className="admin-accordion-icon">
                    {productionApiResponseExpanded ? '▲' : '▼'}
                  </span>
                  <span className="admin-accordion-title">API Response (Production)</span>
                </div>
                <div className="admin-accordion-content api-response-content">
                  {renderApiResponseContent(productionApiResponse, productionActiveTickets, 'production')}
                </div>
              </div>
              <div className={`admin-accordion nested ${productionDbLogExpanded ? 'expanded' : ''}`}>
                <div className="admin-accordion-header" onClick={() => setProductionDbLogExpanded(!productionDbLogExpanded)}>
                  <span className="admin-accordion-icon">
                    {productionDbLogExpanded ? '▲' : '▼'}
                  </span>
                  <span className="admin-accordion-title">Database Operations (Production)</span>
                </div>
                <div className="admin-accordion-content">
                  {renderDbLogContent(productionDbLog, productionTicketEvents)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Admin;

